import type React from 'react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';
import styles from './VulnerabilityDetail.module.css';

interface FalsePositiveModalProps {
  vulnerabilityId: string;
  schemaId?: string;
  onClose: () => void;
  onSubmitted: () => void;
  onFindSimilar: (reason: string, summary: string) => void;
}

export const FalsePositiveModal: React.FC<FalsePositiveModalProps> = ({
  vulnerabilityId,
  schemaId,
  onClose,
  onSubmitted,
  onFindSimilar,
}) => {
  const [reason, setReason] = useState('');
  const [summary, setSummary] = useState('');
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

  const handleGenerateSummary = async () => {
    if (!reason.trim()) {
      toast.warning('Please provide a reason first');
      return;
    }

    setIsGeneratingSummary(true);
    try {
      let url = `${API_BASE_URL}/api/vulnerabilities/${vulnerabilityId}/generate-fp-summary`;
      if (schemaId) url += `?schema_id=${schemaId}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });

      if (response.ok) {
        const result = await response.json();
        setSummary(result.data.summary);
        toast.success('Summary generated successfully');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to generate summary');
      }
    } catch (error) {
      console.error('Error generating summary:', error);
      toast.error('Failed to generate summary');
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.warning('Please provide a reason');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/vulnerabilities/${vulnerabilityId}/mark-false-positive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, summary }),
      });

      if (response.ok) {
        toast.success('Marked as false positive');
        onSubmitted();
      } else {
        toast.error('Failed to mark as false positive');
      }
    } catch (error) {
      console.error('Error marking false positive:', error);
      toast.error('Failed to mark as false positive');
    }
  };

  return (
    <div className={styles.modal} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Mark as False Positive</h3>
          <button className={styles.actionButton} data-variant="secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.section}>
            <div className={styles.fieldLabel}>Reason (Required)</div>
            <textarea
              className={styles.textArea}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why this is a false positive..."
            />
          </div>

          <div className={styles.section}>
            <div className={styles.fieldRow}>
              <div className={styles.infoLabel}>AI-Generated Summary (500 chars)</div>
              <button
                className={styles.actionButton}
                onClick={handleGenerateSummary}
                disabled={isGeneratingSummary || !reason.trim()}
              >
                {isGeneratingSummary ? 'Generating...' : 'Generate Summary'}
              </button>
            </div>
            <textarea
              className={styles.textArea}
              value={summary}
              onChange={(e) => setSummary(e.target.value.substring(0, 500))}
              placeholder="Click 'Generate Summary' to create an AI summary..."
              maxLength={500}
            />
            <div className={styles.charCount}>{summary.length}/500 characters</div>
          </div>

          {summary && (
            <div className={styles.section}>
              <button className={styles.actionButton} onClick={() => onFindSimilar(reason, summary)}>
                Find Similar Vulnerabilities
              </button>
            </div>
          )}
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.actionButton} data-variant="secondary" onClick={onClose}>
            Cancel
          </button>
          <button className={styles.actionButton} onClick={handleSubmit} disabled={!reason.trim()}>
            Mark as False Positive
          </button>
        </div>
      </div>
    </div>
  );
};
